/**
 * UsersPermissionsTab — F10 T10.10 (panel klienta, Settings).
 *
 * Macierz user × firma × rola + dosypane uprawnienia.
 *
 * Widok:
 *   - Tabela userow: nazwa+email | chipy "firma: rola" | akcja "tarcza" otwiera inline panel.
 *   - Inline panel pod wybranym userem (Callpage UX):
 *     - lista firm tego usera, per firma:
 *       - dropdown roli (auto-save, kazda zmiana = osobny POST)
 *       - akordeon "dosypane uprawnienia" pogrupowany per modul
 *         (uprawnienia z roli sa zaznaczone i nieklikalne — plan zarzadu §834: brak
 *         revoked_permissions, klikalne tylko spoza roli)
 *       - kosz "zabierz dostep do tej firmy" (DELETE, 409 LAST_COMPANY_ACCESS = toast)
 *     - stopka: "Dodaj firme" (picker firm spoza juz przypisanych)
 *
 * Decyzje produktowe (T10.7b backend, T10.10 frontend) — patrz CLAUDE.md tryb po-ludzku:
 *   - per-wiersz auto-save (decyzja 2b z T10.7b briefingu)
 *   - reset dosypanych przy zmianie roli (UX z Callpage)
 *   - super-userzy widoczni z badge "Super-user" — bez panelu uprawnien (bypassuja wszystko)
 *   - 409 LAST_COMPANY_ACCESS / 409 ROLE_IN_USE → toast (nie modal)
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Shield,
  Trash2,
  User as UserIcon,
  UserPlus,
  X,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { InfoHint } from '@/components/InfoHint';
import { MultiSelect } from '@/components/MultiSelect';
import { Spinner } from '@/components/Spinner';
import { CompanyRolesEditor } from '@/components/users/CompanyRolesEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  type CompanyListItem,
  type PermissionDefinition,
  type RoleTemplate,
  useCompanies,
  USER_PERMISSIONS_KEY,
  type UserCompanyAccess,
  type UserListItem,
  useRoleTemplates,
  USERS_LIST_KEY,
  useUserPermissions,
  useUsersList,
} from '@/lib/use-permissions';

interface AssignMemberInput {
  companyId: string;
  userId: string;
  /** Komplet ról w kolejności (ADR-0103). Gdy podany — wygrywa z `roleTemplateId`. */
  roleTemplateIds?: string[];
  roleTemplateId?: string;
}

interface RevokeMemberInput {
  companyId: string;
  userId: string;
}

export function UsersPermissionsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showAddCompany, setShowAddCompany] = useState(false);

  const usersQ = useUsersList();
  const companiesQ = useCompanies();
  const rolesQ = useRoleTemplates();
  const userPermsQ = useUserPermissions(selectedUserId);

  const selectedUser = useMemo(
    () => usersQ.data?.users.find((u) => u.id === selectedUserId) ?? null,
    [usersQ.data, selectedUserId],
  );

  const assignMutation = useMutation({
    mutationFn: async (input: AssignMemberInput) => {
      const body: Record<string, unknown> = { userId: input.userId };
      if (input.roleTemplateIds) body.roleTemplateIds = input.roleTemplateIds;
      else body.roleTemplateId = input.roleTemplateId;
      const res = await api.post(`/companies/${input.companyId}/members`, body);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(variables.userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({
        variant: 'success',
        title: t('tenant.permissions.toast.assignSuccess'),
      });
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.assignError'),
        description: extractErrorCode(err),
      });
    },
  });

  // Wsadowe przypisanie do wielu firm jedną rolą (multi-select) — jedno żądanie.
  const batchAssignMutation = useMutation({
    mutationFn: async (input: { userId: string; companyIds: string[]; roleKeys: string[] }) => {
      const res = await api.post('/companies/members/batch', input);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(variables.userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.permissions.toast.assignSuccess') });
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.assignError'),
        description: extractErrorCode(err),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (input: RevokeMemberInput) => {
      const res = await api.delete(`/companies/${input.companyId}/members/${input.userId}`);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(variables.userId) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({
        variant: 'success',
        title: t('tenant.permissions.toast.revokeSuccess'),
      });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'LAST_COMPANY_ACCESS') {
        toast.show({
          variant: 'warning',
          title: t('tenant.permissions.toast.lastCompany.title'),
          description: t('tenant.permissions.toast.lastCompany.description'),
          duration: 7000,
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.revokeError'),
        description: code,
      });
    },
  });

  if (usersQ.isLoading || companiesQ.isLoading || rolesQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <Spinner />
      </div>
    );
  }

  if (usersQ.isError || companiesQ.isError || rolesQ.isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
      >
        {t('tenant.permissions.error')}
      </div>
    );
  }

  const users = usersQ.data?.users ?? [];
  const companies = companiesQ.data?.companies ?? [];
  const roles = rolesQ.data?.roleTemplates ?? [];
  const userCompaniesAccess = userPermsQ.data?.companies ?? [];

  const availableCompaniesForUser = userCompaniesAccess
    ? companies.filter((c) => !userCompaniesAccess.some((a) => a.companyId === c.id))
    : companies;

  function handleSelectUser(userId: string) {
    setSelectedUserId(userId === selectedUserId ? null : userId);
    setShowAddCompany(false);
  }

  function handleRolesChange(access: UserCompanyAccess, roleTemplateIds: string[]) {
    if (!selectedUserId) return;
    assignMutation.mutate({
      companyId: access.companyId,
      userId: selectedUserId,
      roleTemplateIds,
    });
  }

  function handleAddCompany(companyIds: string[], roleKeys: string[]) {
    if (!selectedUserId) return;
    batchAssignMutation.mutate({ userId: selectedUserId, companyIds, roleKeys });
    setShowAddCompany(false);
  }

  function handleRevokeCompany(access: UserCompanyAccess) {
    if (!selectedUserId) return;
    revokeMutation.mutate({ companyId: access.companyId, userId: selectedUserId });
  }

  return (
    <div className="space-y-4" data-testid="users-permissions-tab">
      {/* Przycisk „Dodaj użytkownika" — link do istniejącej strony tworzenia usera
          (/users/new), żeby zakładać usera także z tego ekranu (prośba Aleksa). */}
      <div className="flex items-center justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/users/new')}
          leftIcon={<UserPlus size={16} aria-hidden="true" />}
          data-testid="permissions-add-user"
        >
          {t('tenant.permissions.addUser')}
        </Button>
      </div>
      <Card padding="md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-permissions-table">
            <thead>
              <tr className="border-b border-border-secondary text-left text-xs uppercase text-text-tertiary">
                <th className="px-3 py-2 font-medium">{t('tenant.permissions.col.user')}</th>
                <th className="px-3 py-2 font-medium">{t('tenant.permissions.col.companies')}</th>
                <th className="px-3 py-2 font-medium text-right">
                  {t('tenant.permissions.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-text-tertiary">
                    {t('tenant.permissions.empty')}
                  </td>
                </tr>
              )}
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  selected={user.id === selectedUserId}
                  onSelect={() => handleSelectUser(user.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedUser && (
        <Card padding="md" data-testid="users-permissions-panel">
          <div className="flex items-center justify-between border-b border-border-secondary pb-3 mb-3">
            <div>
              <h3 className="text-base font-semibold text-text-primary">
                {selectedUser.displayName ?? selectedUser.email}
              </h3>
              <p className="text-xs text-text-tertiary">{selectedUser.email}</p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedUserId(null)}
              className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
              aria-label={t('common.close')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          {selectedUser.isSuperUser ? (
            <div
              role="status"
              className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
              data-testid="super-user-info"
            >
              <Shield className="mb-2 h-5 w-5" aria-hidden="true" />
              <strong className="block">{t('tenant.permissions.superUser.title')}</strong>
              <span>{t('tenant.permissions.superUser.description')}</span>
            </div>
          ) : userPermsQ.isLoading ? (
            <div className="flex items-center justify-center py-6" role="status">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-3">
              {userCompaniesAccess.length === 0 && !showAddCompany && (
                <p className="text-sm text-text-tertiary">{t('tenant.permissions.noCompanies')}</p>
              )}
              {userCompaniesAccess.length > 0 && (
                <InfoHint>{t('tenant.users.roles.hint')}</InfoHint>
              )}
              {userCompaniesAccess.map((access) => (
                <CompanyAccessRow
                  key={access.companyId}
                  access={access}
                  roles={roles}
                  onRolesChange={(roleIds) => handleRolesChange(access, roleIds)}
                  onRevoke={() => handleRevokeCompany(access)}
                  busy={assignMutation.isPending || revokeMutation.isPending}
                />
              ))}

              {showAddCompany ? (
                <AddCompanyForm
                  available={availableCompaniesForUser}
                  roles={roles}
                  onCancel={() => setShowAddCompany(false)}
                  onSubmit={handleAddCompany}
                />
              ) : availableCompaniesForUser.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowAddCompany(true)}
                  className="flex items-center gap-2 rounded-lg border border-dashed border-border-secondary px-3 py-2 text-sm text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20"
                  data-testid="add-company-btn"
                >
                  <Plus size={16} aria-hidden="true" />
                  {t('tenant.permissions.addCompany.button')}
                </button>
              ) : null}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// UserRow
// ============================================================================

function UserRow({
  user,
  selected,
  onSelect,
}: {
  user: UserListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr
      className={[
        'border-b border-border-secondary transition-colors',
        selected ? 'bg-accent-50 dark:bg-accent-900/10' : 'hover:bg-bg-tertiary',
      ].join(' ')}
      data-testid={`user-row-${user.id}`}
    >
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary">
            <UserIcon className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-text-primary truncate">
              {user.displayName ?? user.email}
            </div>
            <div className="text-xs text-text-tertiary truncate">{user.email}</div>
          </div>
          {user.isSuperUser && (
            <Badge color="blue">{t('tenant.permissions.badge.superUser')}</Badge>
          )}
          {user.status === 'suspended' && (
            <Badge color="amber">{t('tenant.permissions.badge.suspended')}</Badge>
          )}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1">
          {user.companies.length === 0 ? (
            <span className="text-xs text-text-tertiary">
              {t('tenant.permissions.noCompanies')}
            </span>
          ) : (
            user.companies.map((c) => (
              <span
                key={c.id}
                className="rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-text-secondary"
              >
                {c.shortName ?? c.legalName}
              </span>
            ))
          )}
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <Button
          variant="secondary"
          size="sm"
          onClick={onSelect}
          leftIcon={<Shield className="h-4 w-4" aria-hidden="true" />}
          aria-label={t('tenant.permissions.actions.changeRole')}
          data-testid={`open-panel-${user.id}`}
        >
          {t('tenant.permissions.actions.changeRole')}
        </Button>
      </td>
    </tr>
  );
}

// ============================================================================
// CompanyAccessRow
// ============================================================================

export function CompanyAccessRow({
  access,
  roles,
  onRolesChange,
  onRevoke,
  busy,
}: {
  access: UserCompanyAccess;
  roles: RoleTemplate[];
  /** Komplet ról osoby w tej firmie po zmianie, w kolejności (pierwsza = główna). */
  onRolesChange: (roleIds: string[]) => void;
  onRevoke: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();

  // Role są per firma — do wyboru wyłącznie role TEJ firmy.
  const companyRoles = useMemo(
    () => roles.filter((r) => r.companyId === access.companyId),
    [roles, access.companyId],
  );
  const assignedRoles =
    access.roles ??
    (access.roleTemplateId
      ? [{ id: access.roleTemplateId, name: access.roleTemplateName ?? '' }]
      : []);

  // Dawna rozwijana lista „dosypanych uprawnień" zniknęła — indywidualną dosypkę serwer wycofał
  // w F24 (każde kliknięcie kończyło się błędem), a szczegółowe prawa nadaje Centrum uprawnień.
  return (
    <div
      className="rounded-lg border border-border-secondary p-3"
      data-testid={`company-access-${access.companyId}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-text-primary truncate">
            {access.shortName ?? access.legalName}
          </div>
          {access.shortName && (
            <div className="text-xs text-text-tertiary truncate">{access.legalName}</div>
          )}
        </div>
        <CompanyRolesEditor
          companyId={access.companyId}
          assigned={assignedRoles}
          available={companyRoles}
          onChange={onRolesChange}
          busy={busy}
        />
        <IconButton
          size="sm"
          variant="danger"
          onClick={onRevoke}
          disabled={busy}
          aria-label={t('tenant.permissions.actions.revokeCompany')}
          data-testid={`revoke-${access.companyId}`}
        >
          <Trash2 aria-hidden="true" />
        </IconButton>
      </div>
    </div>
  );
}

// ============================================================================
// AddCompanyForm — picker firm + role (wiele, w kolejności)
// ============================================================================

/**
 * Linie rodowe ról (ta sama rola w każdej firmie ma inny identyfikator, wspólny `roleKey`) —
 * formularz dodaje osobę do KILKU firm naraz, więc wybiera się rolę „jako taką", a serwer sam
 * przypina kopię właściwą dla każdej firmy (wsad `/companies/members/batch` z `roleKeys`).
 */
function roleLineages(
  roles: RoleTemplate[],
): Array<{ id: string; name: string; isSystem: boolean }> {
  const byKey = new Map<string, { id: string; name: string; isSystem: boolean }>();
  for (const r of roles) {
    const key = r.roleKey ?? r.id;
    if (!byKey.has(key)) byKey.set(key, { id: key, name: r.name, isSystem: r.isSystem });
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
}

export function AddCompanyForm({
  available,
  roles,
  onCancel,
  onSubmit,
}: {
  available: CompanyListItem[];
  roles: RoleTemplate[];
  onCancel: () => void;
  /** Wszystkie zaznaczone firmy dostają ten sam zestaw ról (linie rodowe, w kolejności). */
  onSubmit: (companyIds: string[], roleKeys: string[]) => void;
}) {
  const { t } = useTranslation();
  const lineages = useMemo(() => roleLineages(roles), [roles]);
  const defaultRole =
    roles.find((r) => r.isDefaultMember) ?? roles.find((r) => r.isSystem) ?? roles[0];
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [roleKeys, setRoleKeys] = useState<string[]>(
    defaultRole ? [defaultRole.roleKey ?? defaultRole.id] : [],
  );

  const nameByKey = new Map(lineages.map((l) => [l.id, l.name]));
  const allSelected = available.length > 0 && companyIds.length === available.length;
  const canSubmit = companyIds.length > 0 && roleKeys.length > 0;

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-dashed border-accent-300 bg-accent-50/50 p-3 dark:bg-accent-900/10"
      data-testid="add-company-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(companyIds, roleKeys);
      }}
    >
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="block text-xs text-text-tertiary">
            {t('tenant.permissions.addCompany.companyLabel')}
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto p-0 text-xs hover:bg-transparent hover:underline"
            disabled={available.length === 0}
            data-testid="add-company-select-all"
            onClick={() => setCompanyIds(allSelected ? [] : available.map((c) => c.id))}
          >
            {allSelected
              ? t('tenant.permissions.addCompany.clearAll')
              : t('tenant.permissions.addCompany.selectAll')}
          </Button>
        </div>
        <MultiSelect
          value={companyIds}
          onChange={setCompanyIds}
          options={available.map((c) => ({ value: c.id, label: c.legalName }))}
          label={undefined}
          data-testid="add-company-select"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-text-tertiary">
          {t('tenant.permissions.addCompany.rolesLabel')}
        </label>
        <CompanyRolesEditor
          companyId="new-company-access"
          assigned={roleKeys.map((k) => ({ id: k, name: nameByKey.get(k) ?? '' }))}
          available={lineages}
          onChange={setRoleKeys}
        />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!canSubmit}
          data-testid="add-company-submit"
        >
          {t('tenant.permissions.addCompany.submit')}
        </Button>
      </div>
    </form>
  );
}

// ============================================================================
// helpers
// ============================================================================

function extractErrorCode(err: unknown): string | undefined {
  if (isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object' && 'error' in data) {
      const code = (data as { error?: unknown }).error;
      if (typeof code === 'string') return code;
    }
  }
  return undefined;
}
